import { useEffect, useState } from 'react';

import { bootstrapFromIdb } from './bootstrap-from-idb.js';
import type { BootstrapResult } from './bootstrap-result.type.js';

/**
 * Status des Recovery-Bootstraps. Nutzbar als Diskriminante in der
 * Render-Logik (`pending` → Skeleton, `recovered` → LOADING + Polling,
 * andere → frischer Vorgang).
 */
export type RecoveryStatus = 'pending' | 'no-recovery' | 'recovered' | 'expired';

/**
 * Aufgelöster Recovery-State. `data` ist nur im `recovered`-Branch gesetzt
 * und enthält die fürs Polling benötigten Felder (Privatkey + tenantId +
 * queueItemId).
 */
export interface RecoveryState {
  readonly status: RecoveryStatus;
  readonly data?: {
    readonly tenantId: string;
    readonly queueItemId: string;
    readonly privateKey: CryptoKey;
  };
}

/**
 * Optionen für `useRecoveryBootstrap`. `now` ist optional — Default ist
 * `Date.now()`; Tests injizieren stabile Werte.
 */
interface UseRecoveryBootstrapOptions {
  readonly now?: () => number;
}

/**
 * Recovery-Bootstrap als React-Hook.
 *
 * Läuft einmal pro Mount-Identität; StrictMode-Doppel-Mount setzt das
 * `cancelled`-Flag und verhindert, dass das Ergebnis des ersten Laufs ein
 * Render des zweiten Laufs überschreibt. IDB wird trotz Doppel-Effekt nur
 * einmal angefasst (idempotente Funktion, deterministische Resultate).
 */
export function useRecoveryBootstrap(options: UseRecoveryBootstrapOptions = {}): RecoveryState {
  const [state, setState] = useState<RecoveryState>({ status: 'pending' });

  useEffect(() => {
    let cancelled = false;
    const now = (options.now ?? Date.now)();
    bootstrapFromIdb({ now }).then(
      (result) => {
        if (!cancelled) {
          setState(mapResultToState(result));
        }
      },
      () => {
        if (!cancelled) {
          setState({ status: 'no-recovery' });
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [options.now]);

  return state;
}

function mapResultToState(result: BootstrapResult): RecoveryState {
  if (result.kind === 'recovered') {
    return {
      status: 'recovered',
      data: {
        tenantId: result.tenantId,
        queueItemId: result.queueItemId,
        privateKey: result.privateKey,
      },
    };
  }
  return { status: result.kind };
}
